/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { ComponentLike } from '@glint/template';
import CampaignModel from 'cio/models/campaign';
import { MetricDataPoint, Periods } from 'cio/utils/metrics/common';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { changeFromLast, trendPositivity, trendIconClass, trendTextClass } from 'cio/utils/metrics/trends';
import actionInfo, { DELIVERY_STATES_AND_ACTIONS_PER_TYPE } from 'cio/utils/action-info';
import { underscore } from '@ember/string';
import { ActionType } from 'cio/models/action';
import { Grouping } from 'cio/utils/metrics/resolution';
import { mapPercentDataPoints } from 'cio/utils/graphs';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import { action } from '@ember/object';
import SettingsService from 'cio/services/settings';
import { MeasureType } from 'cio/utils/metrics/measure';

interface CampaignOverviewPageSparklinesSectionMetricCardArgs {
  title: string;
  measure: string;
  measureType: MeasureType;
  graphTitle: string;
  graphColor: string;
  graphGradient: string;
  isRate: boolean;
  data?: MetricDataPoint[];
  periodTotal: number;
  campaign: CampaignModel;
  timeRange: DateRange;
  timePeriod: Periods;
  grouping: Grouping;
  isLoadingMetrics: boolean;
  chartOptions?: import('highcharts').Options;
  helpTooltipComponent?: ComponentLike<any>;
  channels: string[];
}

export default class CampaignOverviewSparklinesSectionMetricCardPage extends Component<CampaignOverviewPageSparklinesSectionMetricCardArgs> {
  @service analytics!: AnalyticsService;
  @service settings!: SettingsService;

  get timezone() {
    if (this.args.measureType === MeasureType.DeliveryMetrics) {
      return this.settings.environmentSettings.metricsTimezone;
    } else if (this.args.measureType === MeasureType.JourneyMetrics) {
      return this.settings.environmentSettings.journeyMetricsTimezone;
    }

    return null;
  }

  get chartData() {
    if (this.args.isRate) {
      return mapPercentDataPoints(this.args.data ?? []);
    }

    return this.args.data ?? [];
  }

  get changeFromLast() {
    return changeFromLast(this.args.data ?? []);
  }

  get trendPositivity() {
    if (!this.changeFromLast) {
      return null;
    }

    return trendPositivity(this.args.measure, this.changeFromLast);
  }

  get trendIconClass() {
    return trendIconClass(this.trendPositivity);
  }

  get trendTextClass() {
    return trendTextClass(this.trendPositivity);
  }

  get periodDescriptor() {
    return {
      [Grouping.Hourly]: 'hour',
      [Grouping.Daily]: 'day',
      [Grouping.Weekly]: 'week',
      [Grouping.Monthly]: 'month',
    }[this.args.grouping];
  }

  get channelActionTypes() {
    return this.args.channels.map((channel) => `${channel}_action`);
  }

  get availableActionInfos() {
    return Object.entries(DELIVERY_STATES_AND_ACTIONS_PER_TYPE)
      .filter(([actionType, measures]) => {
        if (!this.channelActionTypes.includes(actionType)) {
          return false;
        }

        if (measures.includes(underscore(this.args.measure))) {
          return true;
        }

        let info = actionInfo(actionType as ActionType, { canConvert: true });
        return this.args.measure === 'converted' && info != null && info.optionallyConverts === true;
      })
      .map(([actionType, _]) => actionInfo(actionType as ActionType));
  }

  get isAllEmpty() {
    return this.chartData.length === 0 || !this.chartData.some((p) => p.y != null);
  }

  @action
  onTrackHover(time: number) {
    this.analytics.track('campaign', 'overview_chart_hover', {
      model: this.args.campaign,
      time,
      measure: this.args.measure,
      measureType: this.args.measureType,
    });
  }
}
